//# sourceMappingURL=modem.mjs.map
/// <reference types="./modem.d.mts" />
import * as $decode from "../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import { None } from "../gleam_stdlib/gleam/option.mjs";
import * as $uri from "../gleam_stdlib/gleam/uri.mjs";
import { Uri } from "../gleam_stdlib/gleam/uri.mjs";
import * as $redraw from "../redraw/redraw.mjs";
import * as $event from "../redraw/redraw/event.mjs";
import * as $attribute from "../redraw_dom/redraw/dom/attribute.mjs";
import * as $mouse from "../redraw_dom/redraw/dom/event/mouse.mjs";
import * as $events from "../redraw_dom/redraw/dom/events.mjs";
import * as $effect from "../redraw_lustre/redraw/lustre/effect.mjs";
import * as $css from "../sketch/sketch/css.mjs";
import * as $h from "../sketch_redraw/sketch/redraw/dom/hooks/html.mjs";
import { Ok, toList, prepend as listPrepend, CustomType as $CustomType } from "./gleam.mjs";
import {
  do_initial_uri as initial_uri,
  do_init,
  do_init as do_advanced,
  do_push,
  do_replace,
  do_load,
  do_forward,
  do_back,
} from "./modem.ffi.mjs";

export { initial_uri };

export class Options extends $CustomType {
  constructor(handle_internal_links, handle_external_links) {
    super();
    this.handle_internal_links = handle_internal_links;
    this.handle_external_links = handle_external_links;
  }
}
export const Options$Options = (handle_internal_links, handle_external_links) =>
  new Options(handle_internal_links, handle_external_links);
export const Options$isOptions = (value) => value instanceof Options;
export const Options$Options$handle_internal_links = (value) =>
  value.handle_internal_links;
export const Options$Options$0 = (value) => value.handle_internal_links;
export const Options$Options$handle_external_links = (value) =>
  value.handle_external_links;
export const Options$Options$1 = (value) => value.handle_external_links;

const relative = /* @__PURE__ */ new Uri(
  /* @__PURE__ */ new None(),
  /* @__PURE__ */ new None(),
  /* @__PURE__ */ new None(),
  /* @__PURE__ */ new None(),
  "",
  /* @__PURE__ */ new None(),
  /* @__PURE__ */ new None(),
);

/**
 * Initialise a simple modem that intercepts internal links and sends them to
 * your update function through the provided handler.
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function init(handler) {
  return $effect.from(
    (dispatch, _) => {
      return do_init(
        (uri) => {
          let _pipe = uri;
          let _pipe$1 = handler(_pipe);
          dispatch(_pipe$1);
          return undefined;
        },
      );
    },
  );
}

/**
 * Initialise an advanced modem that lets you configure what types of links to
 * intercept. Take a look at the [`Options`](#options) type for info on what
 * can be configured.
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function advanced(options, handler) {
  return $effect.from(
    (dispatch, _) => {
      return ((_capture) => { return do_advanced(_capture, options); })(
        (uri) => {
          let _pipe = uri;
          let _pipe$1 = handler(_pipe);
          dispatch(_pipe$1);
          return undefined;
        },
      );
    },
  );
}

/**
 * Push a new relative route onto the browser's history stack. This will not
 * trigger a full page reload.
 *
 * **Note**: if you push a new uri while the user has navigated using the back
 * or forward buttons, you will clear any forward history in the stack!
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function push(path, query, fragment) {
  return do_push(
    new Uri(
      relative.scheme,
      relative.userinfo,
      relative.host,
      relative.port,
      path,
      query,
      fragment,
    ),
  );
}

function on_click() {
  return $events.on_click(
    (event) => {
      let event$1 = $mouse.as_event(event);
      $event.stop_propagation(event$1);
      $event.prevent_default(event$1);
      let target = $event.target(event$1);
      let $ = $decode.run(target, $decode.at(toList(["href"]), $decode.string));
      if ($ instanceof Ok) {
        let href = $[0];
        let $1 = $uri.parse(href);
        if ($1 instanceof Ok) {
          let uri = $1[0];
          return do_push(uri);
        } else {
          return undefined;
        }
      } else {
        return undefined;
      }
    },
  );
}

export function nav_link(attrs, children, style) {
  let on_click$1 = on_click();
  return $h.a(listPrepend(on_click$1, attrs), children, style);
}

/**
 * Replace the current uri in the browser's history stack with a new relative
 * route. This will not trigger a full page reload.
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function replace(path, query, fragment) {
  return $effect.from(
    (_, _1) => {
      return do_replace(
        new Uri(
          relative.scheme,
          relative.userinfo,
          relative.host,
          relative.port,
          path,
          query,
          fragment,
        ),
      );
    },
  );
}

/**
 * Load a new uri. This will always trigger a full page reload even if the uri
 * is relative or the same as the current page.
 *
 * **Note**: if you load a new uri while the user has navigated using the back
 * or forward buttons, you will clear any forward history in the stack!
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function load(uri) {
  return $effect.from((_, _1) => { return do_load(uri); });
}

/**
 * The browser maintains a history stack of all the url's the user has visited.
 * This function lets you move forward the given number of steps in that stack.
 * If you reach the end of the stack, further attempts to go forward will do
 * nothing (unfortunately time travel is not quite possible yet).
 *
 * **Note**: you can go _too far forward_ and end up navigating the user off your
 * app if you're not careful.
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function forward(steps) {
  return $effect.from((_, _1) => { return do_forward(steps); });
}

/**
 * The browser maintains a history stack of all the url's the user has visited.
 * This function lets you move back the given number of steps in that stack.
 * If you reach the beginning of the stack, further attempts to go back will do
 * nothing (unfortunately time travel is not quite possible yet).
 *
 * **Note**: if you navigate back and then [`push`](#push) a new url, you will
 * clear the forward history of the stack.
 *
 * **Note**: you can go _too far back_ and end up navigating the user off your
 * app if you're not careful.
 *
 * > **Note**: this effect is only meaningful in the browser. When executed in
 * > a backend JavaScript environment or in Erlang this effect will always be
 * > equivalent to `effect.none()`
 */
export function back(steps) {
  return $effect.from((_, _1) => { return do_back(steps); });
}
